import React, {useState, useEffect, useCallback, useMemo, useRef} from 'react';
import ChartContent from './ChartContent';
import './ChartsContainer.css';
import { logDebug } from "../../../utils/logger";
import ChartButtons from "./ChartButtons";
import {useAppContext} from "../../../AppDataContext";

interface ChartButtonProps {
  symbols: string[];
  interval?: string;
  timezone?: string;
  theme?: string;
  range?: string;
  compared_symbols: string[];
  studies?: string[];
  action: string;
  chartKey?: string;
  style?: string;
  volumeVisible?: boolean;
}

const MemoizedChartButtons = React.memo(ChartButtons);
const MemoizedChartContent = React.memo(ChartContent);

const ChartsContainer: React.FC = (
) => {

  const [activeIndex, setActiveIndex] = useState<number | null>(0);
  const appContext = useAppContext();
  const AppDataContext = useRef(appContext?.actionDataContextProps).current;

let lastChartIndex = -1
if(AppDataContext?.length)
  for(let i = AppDataContext?.length||0  ; i >= 0 ; i--){
      if(AppDataContext[i] && AppDataContext[i].action && AppDataContext[i].action.toLowerCase().includes("chart")){
            lastChartIndex = i;
            break;
      }
}

  const memoizedSymbols = useMemo(() => AppDataContext?.[lastChartIndex||0]?.inputs?.symbols, [AppDataContext?.[lastChartIndex||0].inputs.symbols]);
  const memoizedComparedSymbols = useMemo(() => AppDataContext?.[lastChartIndex||0]?.inputs.comparison_symbols, [AppDataContext?.[lastChartIndex||0]?.inputs.comparison_symbols]);
  const memoizedChartData = useMemo(() => memoizedSymbols.map(() => null), [memoizedSymbols]);
  const [chartData, setChartData] = useState<any[]>(memoizedChartData);
  const memoizedStudies = useMemo(() => AppDataContext?.[lastChartIndex||0]?.inputs.indicators, [AppDataContext?.[lastChartIndex||0].inputs.indicators]);

  useEffect(() => {
    logDebug("chartButtons-", memoizedSymbols);
    if (activeIndex !== null && !chartData[activeIndex]) {
      // Simulate fetching chart data
      const fetchData = async () => {
        // Replace with actual data fetching logic
        const data = await new Promise((resolve) => setTimeout(() => resolve({loaded: true}), 500));
        setChartData(prevData => {
          const newData = [...prevData];
          if (newData[activeIndex] !== data) {
            newData[activeIndex] = data;
          }
          return newData;
        });
      };
      fetchData();
    }
  }, [activeIndex, chartData, memoizedSymbols]);

  const handleButtonClick = useCallback((index: number) => {
    setActiveIndex(index);
  }, []);

  if (memoizedSymbols.length === 0) {
    return null;
  }

  // Static variable to track the latest chart ID
  let latestChartId = -1;


  // Check if the current chartKey is greater than the latestChartId
  if (AppDataContext?.[lastChartIndex||0].actionKey && parseInt(AppDataContext?.[lastChartIndex||0].actionKey, 10) <= latestChartId) {
    logDebug('Skipping rendering this chart since it is not the latest');
    return null; // Skip rendering if this is not the latest chart
  }
  logDebug("chartsContainer", memoizedSymbols);
  // Update the static latestChartId
  if (AppDataContext?.[lastChartIndex||0].actionKey) {
    latestChartId = parseInt(AppDataContext?.[lastChartIndex||0].actionKey, 10);
  }

  return (
      <div style={{display: "row", alignItems: "center", gap: "16px"}}>
        <div className="chartWrapper">
          <div className="chartContainer">
            {memoizedSymbols.length > 1 && (
                <div className="buttonContainer">
                  <MemoizedChartButtons
                      symbols={memoizedSymbols}
                      activeIndex={activeIndex}
                      handleButtonClick={handleButtonClick}
                  />
                </div>
            )}
            <div className="chartContent">
              {memoizedSymbols.map((symbol: string, index: number) => (
                  <div key={AppDataContext?.[lastChartIndex||0].actionKey} style={{display: activeIndex === index ? 'block' : 'none'}}>
                    {chartData[index] ? (
                        <MemoizedChartContent
                            chartKey={AppDataContext?.[lastChartIndex||0].actionKey}
                            symbol={symbol}
                            interval={AppDataContext?.[lastChartIndex||0].inputs.interval}
                            timezone={AppDataContext?.[lastChartIndex||0].inputs.time_zone}
                            theme={sessionStorage.getItem('userTheme')?.toString().toLowerCase().includes('dark') ? 'Dark' : 'Light'}
                            range={AppDataContext?.[lastChartIndex||0].inputs.range}
                            compared_symbols={memoizedComparedSymbols}
                            studies={memoizedStudies}
                            action={AppDataContext?.[lastChartIndex||0].action || ""}
                            style={AppDataContext?.[lastChartIndex||0].inputs.style_bar}
                            volumeVisible={AppDataContext?.[lastChartIndex||0].inputs.volume !== "hide"}
                        />
                    ) : (
                        <div key={AppDataContext?.[lastChartIndex||0].actionKey}
                             style={{width: '800px', height: '500px'}}>Loading...</div>
                    )}
                  </div>
              ))}
            </div>
          </div>
        </div>
      </div>
  );
};

export default React.memo(ChartsContainer);